import "../../../assets/scss/machine-card-style.scss";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Spin, message } from "antd";
import {
  DatabaseOutlined,
  DesktopOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { installHost } from "../../server/Hosts";

export interface ProviderHostItem {
  id: string;
  node_name?: string;
  gpu_type?: string;
  product_name?: string;
  total_gpu_count?: number;
  status?: number;
}

export interface AddMachineSelectionListProps {
  hosts: ProviderHostItem[];
  loading?: boolean;
  onInstallSuccess?: () => void;
}

export const AddMachineSelectionList = ({
  hosts,
  loading = false,
  onInstallSuccess,
}: AddMachineSelectionListProps) => {
  const { t } = useTranslation();
  const [installingHostId, setInstallingHostId] = useState<string | null>(null);

  const handleInstallHost = async (host: ProviderHostItem) => {
    if (installingHostId) return;

    setInstallingHostId(host.id);
    try {
      const response = await installHost(host.id);
      const data = response.data;
      if (data.code === 0 || data.ResultCode === "0") {
        message.success(t("Installation started successfully"));
        onInstallSuccess?.();
      } else {
        message.error(data.ResultMessage || t("Installation failed"));
      }
    } catch (err) {
      console.error(err);
      message.error(t("Installation failed"));
    } finally {
      setInstallingHostId(null);
    }
  };

  if (loading) {
    return (
      <div className="add-machine-selection-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!hosts || hosts.length === 0) {
    return (
      <div className="add-machine-selection-empty">
        <p>{t("No machines available for installation.")}</p>
      </div>
    );
  }

  return (
    <div className="add-machine-selection-list">
      <h3 className="add-machine-selection-title">
        {t("Select a Machine to Install")}
      </h3>
      <div className="add-machine-selection-container">
        {hosts.map((host) => (
          <div
            key={host.id}
            className={`add-machine-selection-card ${
              installingHostId === host.id ? "installing" : ""
            }`}
            onClick={() => handleInstallHost(host)}
          >
            {installingHostId === host.id ? (
              <div className="installing-overlay">
                <Spin size="small" />
                <span>{t("Installing...")}</span>
              </div>
            ) : (
              <>
                <div className="card-header">
                  <h4>{host.node_name || t("Unknown Machine")}</h4>
                  <CheckCircleOutlined className="install-icon" />
                </div>
                <div className="card-details">
                  <span className="detail-item">
                    <DesktopOutlined /> ID: {host.id} •{" "}
                    {host.gpu_type === "nv" ? "Nvidia" : host.gpu_type}
                  </span>

                  {host.product_name && (
                    <span className="detail-item">
                      <AppstoreOutlined /> {host.product_name}
                    </span>
                  )}
                  {host.total_gpu_count !== undefined && (
                    <span className="detail-item">
                      <DatabaseOutlined />
                      {t("GPU Total")}: {host.total_gpu_count}
                    </span>
                  )}
                </div>
              </>
            )}
            <div className="card-action-hint">{t("Click to install")}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
